import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Reveal, Chapter } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { name: "", practice: "", email: "", phone: "", message: "" };

const FIELDS = [
  { key: "name", label: "Name", type: "text", testid: "contact-name-input" },
  { key: "practice", label: "Practice Name", type: "text", testid: "contact-practice-input" },
  { key: "email", label: "Email", type: "email", testid: "contact-email-input" },
  { key: "phone", label: "Phone", type: "tel", testid: "contact-phone-input" },
];

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | done
  const [submitError, setSubmitError] = useState("");

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.practice.trim()) next.practice = "Please enter your practice name.";
    if (!form.email.trim()) next.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = "Please enter a valid email address.";
    if (!form.phone.trim()) next.phone = "Please enter your phone number.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    setSubmitError("");
    try {
      await axios.post(`${API}/demo-requests`, {
        name: form.name.trim(),
        practice_name: form.practice.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      setStatus("done");
    } catch {
      setStatus("idle");
      setSubmitError(
        "Something went wrong sending your request. Please try again — or email us directly at ryanaali24@gmail.com."
      );
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="scroll-mt-24 border-t border-ink/10 bg-sand/50 py-24 md:py-32"
    >
      <div className="mx-auto max-w-2xl px-6">
        <Reveal>
          <Chapter number="12" label="Book a Demo" />
        </Reveal>
        <Reveal delay={60}>
          <h2 className="font-display text-3xl font-medium leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Book a Demo
          </h2>
        </Reveal>

        {status === "done" ? (
          <Reveal>
            <div
              data-testid="contact-success-message"
              className="mt-10 rounded-2xl border border-teal-line bg-teal-soft p-10 text-center"
            >
              <CheckCircle2 size={40} className="mx-auto text-teal" strokeWidth={1.6} />
              <h3 className="mt-5 font-display text-2xl font-medium text-ink">
                Thank you, {form.name.split(" ")[0]}.
              </h3>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-ink/70 md:text-base">
                Your demo request has been received. We&apos;ll be in touch
                shortly to arrange a short call and walk through how Leadtochair
                would work for {form.practice}.
              </p>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={120}>
            <form data-testid="contact-form" onSubmit={submit} noValidate className="mt-10">
              <div className="grid gap-6 sm:grid-cols-2">
                {FIELDS.map((f) => (
                  <div key={f.key}>
                    <label
                      htmlFor={f.testid}
                      className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink/60"
                    >
                      {f.label} <span className="text-teal">*</span>
                    </label>
                    <input
                      id={f.testid}
                      data-testid={f.testid}
                      type={f.type}
                      value={form[f.key]}
                      onChange={set(f.key)}
                      className={`w-full rounded-xl border bg-white px-4 py-3.5 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-teal ${
                        errors[f.key] ? "border-red-400" : "border-ink/15"
                      }`}
                      aria-invalid={!!errors[f.key]}
                    />
                    {errors[f.key] && (
                      <p data-testid={`${f.testid}-error`} className="mt-1.5 text-xs text-red-600">
                        {errors[f.key]}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <label
                  htmlFor="contact-message-input"
                  className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink/60"
                >
                  Message <span className="normal-case text-ink/40">(optional)</span>
                </label>
                <textarea
                  id="contact-message-input"
                  data-testid="contact-message-input"
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  className="w-full resize-none rounded-xl border border-ink/15 bg-white px-4 py-3.5 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-teal"
                  placeholder="Anything you'd like us to know about your practice or current setup."
                />
              </div>
              {submitError && (
                <p data-testid="contact-submit-error" className="mt-4 text-sm text-red-600">
                  {submitError}
                </p>
              )}
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={status === "sending"}
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal px-8 py-4 text-sm font-medium tracking-wide text-white transition-colors hover:bg-teal-dark disabled:opacity-70 sm:w-auto"
              >
                {status === "sending" && <Loader2 size={16} className="animate-spin" />}
                Book a Demo
              </button>
              <p
                data-testid="contact-reassurance-line"
                className="mt-6 text-sm leading-relaxed text-ink/55"
              >
                We&apos;ll get back to you to arrange a short call and walk
                through how it would work for your practice.
              </p>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
}
